import { Box } from '@hakimo-ui/hakimo/types';
import { useVideoCurrentTime } from '@hakimo-ui/hakimo/util';
import { useEffect, useMemo, useRef } from 'react';
import { getMediaContentRect } from '../image-panel/special-zones-overlay-util';
import {
  getPolygonLines,
  imageMarkerToClientMarker,
} from '../image-panel/util';
import { getVideoPolygons } from './utils';

interface Props {
  boxes?: Box[];
  videoRect?: DOMRect;
  videoWidth?: number;
  videoHeight?: number;
  videoRef?: React.RefObject<HTMLVideoElement>;
}
export function VideoHighlightBox(props: Props) {
  const { boxes = [], videoRect, videoHeight, videoWidth, videoRef } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const currentSecond = useVideoCurrentTime(videoRef);

  useEffect(() => {
    const video = videoRef?.current;
    let observer: MutationObserver | undefined;

    if (video) {
      const syncTransform = () => {
        const svg = containerRef.current?.children[0] as SVGElement | undefined;
        if (!svg) {
          return;
        }

        svg.style.transform = video.style.transform;
        svg.style.translate = video.style.translate;
      };

      syncTransform();
      observer = new MutationObserver(syncTransform);

      observer.observe(video, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => {
      observer?.disconnect();
    };
  }, [videoRef]);

  const visibleBoxes = useMemo(() => {
    return boxes.filter(
      (box) => box.frame === -1 || box.frame === currentSecond
    );
  }, [boxes, currentSecond]);

  const boxesPolygon = useMemo(
    () => getVideoPolygons(visibleBoxes),
    [visibleBoxes]
  );
  const polygons = useMemo(() => {
    const video = videoRef?.current;
    if (!videoRect || !boxesPolygon) {
      return [];
    }
    const contentRect = video
      ? getMediaContentRect(video, videoRect)
      : {
          width: videoRect.width,
          height: videoRect.height,
          offsetX: 0,
          offsetY: 0,
        };
    return boxesPolygon.map((polygon) => {
      return {
        ...polygon,
        points: polygon.points.map((point) => {
          if (videoHeight && videoWidth && contentRect) {
            const [x, y] = imageMarkerToClientMarker(
              [contentRect.width, contentRect.height],
              [videoWidth, videoHeight],
              { point }
            ).point;
            return [
              x + contentRect.offsetX,
              y + contentRect.offsetY,
            ] as typeof point;
          }

          return point;
        }),
      };
    });
  }, [videoRect, boxesPolygon, videoHeight, videoWidth, videoRef]);
  return <div ref={containerRef}>{getPolygonLines(polygons)}</div>;
}
export default VideoHighlightBox;
